import React, { useState, useRef } from 'react';
import { Button } from './ui/Button';
import { Camera, Upload, X } from 'lucide-react';
interface AvatarUploadProps {
  currentAvatar?: string;
  onAvatarChange: (avatarUrl: string) => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}
export function AvatarUpload({
  currentAvatar,
  onAvatarChange,
  size = 'lg'
}: AvatarUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentAvatar || null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sizes = {
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32',
    xl: 'h-40 w-40'
  };
  const handleFileSelect = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreview(result);
        onAvatarChange(result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleRemove = () => {
    setPreview(null);
    onAvatarChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="flex flex-col items-center space-y-4" data-id="element-48">
      <div className={`${sizes[size]} relative group cursor-pointer`} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={() => fileInputRef.current?.click()} data-id="element-49">
        {preview ? <>
            <img src={preview} alt="Avatar preview" className="h-full w-full rounded-full object-cover border-4 border-white shadow-premium-lg ring-2 ring-slate-200" data-id="element-50" />
            <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-51">
              <Camera className="h-8 w-8 text-white" data-id="element-52" />
            </div>
          </> : <div className={`h-full w-full rounded-full border-4 border-dashed ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-slate-300 bg-slate-50'} flex flex-col items-center justify-center transition-all hover:border-primary-500 hover:bg-primary-50`} data-id="element-53">
            <Upload className={`h-8 w-8 ${isDragging ? 'text-primary-600' : 'text-slate-400'} mb-2`} data-id="element-54" />
            <span className="text-xs text-slate-500 font-medium" data-id="element-55">
              Upload Photo
            </span>
          </div>}

        {preview && <button onClick={e => {
        e.stopPropagation();
        handleRemove();
      }} className="absolute -top-2 -right-2 h-8 w-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors z-10" data-id="element-56">
            <X className="h-4 w-4" data-id="element-57" />
          </button>}
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInput} className="hidden" data-id="element-58" />

      <div className="text-center" data-id="element-59">
        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} type="button" data-id="element-60">
          <Upload className="h-4 w-4 mr-2" data-id="element-61" />
          Choose File
        </Button>
        <p className="text-xs text-slate-500 mt-2" data-id="element-62">
          JPG, PNG or GIF (max. 5MB)
        </p>
      </div>
    </div>;
}